import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { TranslationService } from '../../services/translation.service';

import { AccessPoint, Obstacle } from '../../services/commons/synthetic-grid.service';
import { CoverageSelection } from './coverage-interaction.models';

@Component({
  selector: 'app-coverage-context-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './coverage-context-panel.component.html',
  styleUrls: ['./coverage-context-panel.component.scss']
})
export class CoverageContextPanelComponent {
  protected readonly i18n = inject(TranslationService);
  @Input() selection?: CoverageSelection;
  @Input() accessPoints: AccessPoint[] = [];
  @Input() obstacles: Obstacle[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() antennaHeightChange = new EventEmitter<{ index: number; height: number }>();
  @Output() obstacleMaterialChange = new EventEmitter<{ index: number; material: string; loss: number }>();
  @Output() obstacleLossChange = new EventEmitter<{ index: number; loss: number }>();
  @Output() obstacleThicknessChange = new EventEmitter<{ index: number; thickness: number }>();
  @Output() obstacleHeightChange = new EventEmitter<{ index: number; height: number }>();
  @Output() deleteAntenna = new EventEmitter<number>();
  @Output() deleteObstacle = new EventEmitter<number>();

  protected readonly obstacleMaterials: { value: string; labelKey: string; loss: number }[] = [
    { value: 'concrete', labelKey: 'coverage.materials.concrete', loss: 8 },
    { value: 'drywall', labelKey: 'coverage.materials.drywall', loss: 4 },
    { value: 'glass', labelKey: 'coverage.materials.glass', loss: 6 },
    { value: 'wood', labelKey: 'coverage.materials.wood', loss: 3 },
    { value: 'metal', labelKey: 'coverage.materials.metal', loss: 10 }
  ];

  protected get selectedAntenna(): AccessPoint | undefined {
    if (this.selection?.kind !== 'antenna') {
      return undefined;
    }
    return this.accessPoints[this.selection.index];
  }

  protected get selectedObstacle(): Obstacle | undefined {
    if (this.selection?.kind !== 'obstacle') {
      return undefined;
    }
    return this.obstacles[this.selection.index];
  }

  protected antennaLength(): number {
    const ap = this.selectedAntenna;
    if (!ap) {
      return 0;
    }
    const endX = ap.x2 ?? ap.x;
    const endY = ap.y2 ?? ap.y;
    return Math.hypot(endX - ap.x, endY - ap.y);
  }

  protected obstacleLength(): number {
    const obstacle = this.selectedObstacle;
    if (!obstacle) {
      return 0;
    }
    return Math.hypot(obstacle.x2 - obstacle.x, obstacle.y2 - obstacle.y);
  }

  protected onAntennaHeightInput(event: Event): void {
    if (!this.selection || this.selection.kind !== 'antenna') {
      return;
    }
    const next = Number((event.target as HTMLInputElement).value);
    if (Number.isNaN(next)) {
      return;
    }
    const clamped = Math.max(1, Math.min(15, next));
    this.antennaHeightChange.emit({ index: this.selection.index, height: clamped });
  }

  protected onObstacleMaterialSelect(event: Event): void {
    if (!this.selection || this.selection.kind !== 'obstacle') {
      return;
    }
    const material = (event.target as HTMLSelectElement).value;
    const defaultLoss = this.obstacleMaterials.find((item) => item.value === material)?.loss ?? 6;
    this.obstacleMaterialChange.emit({ index: this.selection.index, material, loss: defaultLoss });
  }

  protected onObstacleLossInput(event: Event): void {
    if (!this.selection || this.selection.kind !== 'obstacle') {
      return;
    }
    const loss = Number((event.target as HTMLInputElement).value);
    if (Number.isNaN(loss)) {
      return;
    }
    const clamped = Math.max(0, Math.min(30, loss));
    this.obstacleLossChange.emit({ index: this.selection.index, loss: clamped });
  }

  protected onObstacleThicknessInput(event: Event): void {
    if (!this.selection || this.selection.kind !== 'obstacle') {
      return;
    }
    const thickness = Number((event.target as HTMLInputElement).value);
    if (Number.isNaN(thickness)) {
      return;
    }
    const clamped = Math.max(5, Math.min(80, thickness));
    this.obstacleThicknessChange.emit({ index: this.selection.index, thickness: clamped });
  }

  protected onObstacleHeightInput(event: Event): void {
    if (!this.selection || this.selection.kind !== 'obstacle') {
      return;
    }
    const height = Number((event.target as HTMLInputElement).value);
    if (Number.isNaN(height)) {
      return;
    }
    const clamped = Math.max(1, Math.min(6, height));
    this.obstacleHeightChange.emit({ index: this.selection.index, height: clamped });
  }

  protected requestDeleteAntenna(): void {
    if (this.selection?.kind !== 'antenna') {
      return;
    }
    this.deleteAntenna.emit(this.selection.index);
  }

  protected requestDeleteObstacle(): void {
    if (this.selection?.kind !== 'obstacle') {
      return;
    }
    this.deleteObstacle.emit(this.selection.index);
  }
}
